import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  Chip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlaceIcon from "@mui/icons-material/Place";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { tokens } from "../../theme";
import { pageContainerSx } from "../../components/ui";
import CheckpointMapEditor from "./CheckpointMapEditor";

const MapDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchMap = async () => {
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/maps/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setMap(await res.json());
      else navigate("/maps");
    } catch {
      navigate("/maps");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  // Checkpoints are stored as the map's full mapCoordinate array — there is no
  // per-checkpoint endpoint, so add/update/delete all read-modify-write the
  // whole array back through PUT /api/maps/:id (role-agnostic, unlike the
  // admin-only v1 map endpoint). See CHECKPOINTS_API.md.
  const saveCheckpoints = async (nextCoordinates) => {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("portalToken");
      const body = new FormData();
      body.append("mapCoordinate", JSON.stringify(nextCoordinates));
      body.append("expectedCheckpointCount", String((map.mapCoordinate || []).length));
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/maps/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Failed to save checkpoint");
        return;
      }
      await fetchMap();
    } catch {
      setError("Failed to save checkpoint");
    } finally {
      setSaving(false);
    }
  };

  const handleAddLocation = (locData) => saveCheckpoints([...(map.mapCoordinate || []), locData]);

  const handleUpdateLocation = (index, locData) => {
    const next = [...(map.mapCoordinate || [])];
    next[index] = locData;
    saveCheckpoints(next);
  };

  const handleDeleteLocationByIndex = (index) => {
    const next = (map.mapCoordinate || []).filter((_, i) => i !== index);
    saveCheckpoints(next);
  };

  const handleDeleteCheckpoint = (name) => {
    const next = (map.mapCoordinate || []).filter((c) => Object.keys(c)[0] !== name);
    saveCheckpoints(next);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: tokens.accent }} />
      </Box>
    );
  }
  if (!map) return null;

  // mapCoordinate is an array of { name: [x, y, z, yaw] } entries.
  const markers = (map.mapCoordinate || []).map((item) => {
    const name = Object.keys(item)[0];
    const v = item[name] || [];
    return { name, x: v[0], y: v[1] };
  });

  const venueName = map.venueId?.name || map.hotelId?.name || null;
  const floorName = map.floorId?.name || null;

  return (
    <Box sx={pageContainerSx()}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/maps")}
        size="small"
        sx={{
          mb: 1.5,
          color: tokens.text2,
          "&:hover": { color: tokens.text, background: tokens.surface2 },
        }}
      >
        All maps
      </Button>

      <Stack direction="row" alignItems="flex-end" gap={1.75} flexWrap="wrap" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h1">{map.name}</Typography>
          <Typography sx={{ fontSize: 13, color: tokens.text2, mt: 0.4 }}>
            {[venueName, floorName].filter(Boolean).join(" · ") || "Floor map"}
            {" · click the map to add a checkpoint"}
          </Typography>
        </Box>
        <Chip
          label={`${markers.length} checkpoint${markers.length === 1 ? "" : "s"}`}
          size="small"
          sx={{ ml: "auto", background: tokens.surface3, color: tokens.text2 }}
        />
      </Stack>

      {error && (
        <Typography sx={{ fontSize: 12, color: "var(--danger, #e5484d)", mb: 1 }}>
          {error}
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 280px" },
          gap: 1.75,
          alignItems: "start",
          height: { md: "calc(100vh - 200px)" },
          minHeight: { md: 420 },
        }}
      >
        {/* Interactive map — click to place/edit a checkpoint */}
        <Card
          sx={{
            overflow: "hidden",
            height: { xs: "calc(100vh - 200px)", md: "100%" },
            minHeight: 420,
            opacity: saving ? 0.7 : 1,
            pointerEvents: saving ? "none" : "auto",
          }}
        >
          <CheckpointMapEditor
            mapId={id}
            locations={map.mapCoordinate || []}
            onAddLocation={handleAddLocation}
            onUpdateLocation={handleUpdateLocation}
            onDeleteLocation={handleDeleteLocationByIndex}
          />
        </Card>

        {/* Markers list — matches the map height; only the list scrolls */}
        <Card
          sx={{
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            height: { xs: "auto", md: "100%" },
            maxHeight: { xs: "calc(100vh - 200px)", md: "none" },
          }}
        >
          <Box sx={{ p: "13px 16px", borderBottom: `1px solid ${tokens.border}`, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>
              Checkpoints
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: tokens.text3, mt: 0.25 }}>
              {markers.length} point{markers.length === 1 ? "" : "s"} · click one on the map to edit
            </Typography>
          </Box>
          {markers.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, color: tokens.text3 }}>
                No checkpoints on this map yet
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                overflowY: "scroll",
                // Always-visible, styled scrollbar
                scrollbarWidth: "thin",
                scrollbarColor: `${tokens.borderStrong} transparent`,
                "&::-webkit-scrollbar": { width: 10 },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: tokens.borderStrong,
                  borderRadius: 8,
                  border: `2px solid ${tokens.surface}`,
                },
                "&::-webkit-scrollbar-thumb:hover": { background: tokens.text3 },
              }}
            >
              {markers.map((mk, i) => (
                <Stack
                  key={`${mk.name}-${i}`}
                  direction="row"
                  alignItems="center"
                  gap={1.25}
                  sx={{
                    px: 2,
                    py: 1.4,
                    borderBottom: i < markers.length - 1 ? `1px solid ${tokens.border}` : "none",
                  }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "7px",
                      background: tokens.accentSoft,
                      color: tokens.accent,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>
                      {mk.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        color: tokens.text3,
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      {typeof mk.x === "number" ? mk.x.toFixed(1) : "—"},{" "}
                      {typeof mk.y === "number" ? mk.y.toFixed(1) : "—"}
                    </Typography>
                  </Box>
                  <PlaceIcon sx={{ fontSize: 16, color: tokens.text3 }} />
                  <IconButton
                    size="small"
                    disabled={saving}
                    onClick={() => handleDeleteCheckpoint(mk.name)}
                    sx={{ color: tokens.text3, "&:hover": { color: "var(--danger, #e5484d)" } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              ))}
            </Box>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default MapDetail;
